<template>
  <div class="sz-home" @mousemove="onMouse">
    <AppNavigation />

    <!-- ═════════════════════════ HERO ═════════════════════════ -->
    <section class="hero">
      <div class="hero-bg" aria-hidden="true">
        <div class="orb orb-a"></div>
        <div class="orb orb-b"></div>
        <div class="orb orb-c"></div>
        <div class="hero-grid"></div>
        <span v-for="n in 18" :key="'sp' + n" class="spark" :style="sparkStyle(n)"></span>
      </div>

      <v-container class="hero-inner">
        <v-row align="center">
          <v-col cols="12" md="6" class="hero-copy">
            <div class="eyebrow in-1">
              <span class="live-dot"></span>
              
            </div>

            <h1 class="hero-title in-2">
              Everything your school runs on,
              <span class="shimmer">in one app.</span>
            </h1>

            <p class="hero-sub in-3">
              Messages, announcements, classrooms, lunch, payments, tickets, sports, care and
              more — one place for every
              <span class="rotator" aria-live="polite">
                <transition name="word" mode="out-in">
                  <span :key="roleWord" class="rotator-word">{{ roleWord }}</span>
                </transition>
              </span>
            </p>

            <div class="hero-ctas in-4">
              <v-btn x-large depressed class="btn-gold btn-shine" to="/demo">
                <v-icon left size="20" color="#1A1408">mdi-calendar-check</v-icon>
                Book a demo
              </v-btn>
              <v-btn x-large text class="btn-ghost" to="/pricing">
                See pricing
                <v-icon right size="18">mdi-arrow-right</v-icon>
              </v-btn>
            </div>

            <div class="hero-trust in-5">
              <span><v-icon small color="#7FC8A0">mdi-check-decagram</v-icon> Free setup</span>
              <span><v-icon small color="#7FC8A0">mdi-check-decagram</v-icon> 30-day money-back</span>
              <span><v-icon small color="#7FC8A0">mdi-check-decagram</v-icon> No contracts</span>
            </div>
          </v-col>

          <v-col cols="12" md="6" class="hero-stage-col">
            <div class="hero-stage" :style="tiltStyle">
              <div class="stage-glow"></div>

              <!-- Floating notifications around the phone -->
              <div
                v-for="(t, i) in toasts"
                :key="'toast' + i"
                class="float-toast"
                :class="['toast-' + i, { show: toastOn[i] }]"
              >
                <div class="toast-ic" :style="{ background: t.color }">
                  <v-icon size="16" color="white">{{ t.icon }}</v-icon>
                </div>
                <div>
                  <div class="toast-t">{{ t.title }}</div>
                  <div class="toast-s">{{ t.sub }}</div>
                </div>
              </div>

              <div class="phone">
                <div class="phone-notch"></div>
                <div class="phone-screen">
                  <div class="ps-status"><span>9:41</span><span><v-icon x-small color="white">mdi-signal</v-icon><v-icon x-small color="white">mdi-wifi</v-icon><v-icon x-small color="white">mdi-battery</v-icon></span></div>

                  <transition name="screen" mode="out-in">
                    <!-- 0 · Parent home -->
                    <div v-if="screen === 0" key="s0" class="ps-body">
                      <div class="ps-hello">Good morning, Dana 👋</div>
                      <div class="ps-sub">Tuesday · Oakridge High School</div>
                      <div class="ps-kids">
                        <div class="ps-kid"><div class="kid-av" style="background:#4facfe">M</div><div><b>Maya</b><small>Grade 10</small></div></div>
                        <div class="ps-kid"><div class="kid-av" style="background:#f7971e">L</div><div><b>Leo</b><small>Grade 7</small></div></div>
                      </div>
                      <div class="ps-card gold">
                        <v-icon size="18" color="#1A1408">mdi-silverware-fork-knife</v-icon>
                        <div><b>Lunch balance</b><small>Maya · topped up today</small></div>
                        <span class="ps-amt">$24.50</span>
                      </div>
                      <div class="ps-card">
                        <v-icon size="18" color="#7FC8A0">mdi-bullhorn</v-icon>
                        <div><b>Science fair is Friday!</b><small>Ms. Rivera · Biology</small></div>
                      </div>
                      <div class="ps-card">
                        <v-icon size="18" color="#E0C27A">mdi-clipboard-check</v-icon>
                        <div><b>Field trip slip</b><small>Sign &amp; pay · due Thu</small></div>
                        <span class="ps-chip">Sign</span>
                      </div>
                      <div class="ps-card">
                        <v-icon size="18" color="#4facfe">mdi-basketball</v-icon>
                        <div><b>Game tonight · 7 PM</b><small>Varsity vs. Lincoln · home</small></div>
                      </div>
                    </div>

                    <!-- 1 · Ticket -->
                    <div v-else-if="screen === 1" key="s1" class="ps-body">
                      <div class="ps-title">My Tickets</div>
                      <div class="ticket">
                        <div class="ticket-top">
                          <small>FRIDAY · 7:00 PM</small>
                          <b>Homecoming Game</b>
                          <span>Oakridge vs. Lincoln</span>
                        </div>
                        <div class="ticket-cut"></div>
                        <div class="qr">
                          <i v-for="(on, q) in qrCells" :key="'q' + q" :class="{ on }"></i>
                          <div class="qr-scan"></div>
                        </div>
                        <div class="ticket-foot">2 × Student · Show at the gate</div>
                      </div>
                    </div>

                    <!-- 2 · Lunch menu -->
                    <div v-else-if="screen === 2" key="s2" class="ps-body">
                      <div class="ps-title">Lunch Menu</div>
                      <div class="week">
                        <div v-for="(d, i) in ['S','M','T','W','T','F','S']" :key="'d' + i" class="wd" :class="{ sel: i === 2 }">{{ d }}<i v-if="i > 0 && i < 6"></i></div>
                      </div>
                      <div class="meal">
                        <div class="meal-h"><v-icon size="15" color="#f7971e">mdi-coffee</v-icon> Breakfast</div>
                        <div class="meal-i">Blueberry pancakes <em>Gluten · Dairy</em></div>
                        <div class="meal-i">Fresh fruit cup</div>
                      </div>
                      <div class="meal">
                        <div class="meal-h"><v-icon size="15" color="#4facfe">mdi-silverware-fork-knife</v-icon> Lunch</div>
                        <div class="meal-i">Chicken tacos <em>Gluten</em></div>
                        <div class="meal-i">Black bean salad</div>
                        <div class="meal-i">Milk or juice <em>Dairy</em></div>
                      </div>
                    </div>

                    <!-- 3 · District directory -->
                    <div v-else key="s3" class="ps-body">
                      <div class="ps-title">District &amp; Schools</div>
                      <div class="ps-sub">Riverside Unified · 4 schools</div>
                      <div v-for="(s, i) in miniSchools" :key="'ms' + i" class="ps-school" :style="{ animationDelay: (i * 0.08) + 's' }">
                        <div class="sch-ic" :style="{ background: s.c }"><v-icon size="15" color="white">{{ s.i }}</v-icon></div>
                        <div><b>{{ s.n }}</b><small>{{ s.k }}</small></div>
                        <v-icon size="16" color="rgba(255,255,255,.35)">mdi-chevron-right</v-icon>
                      </div>
                    </div>
                  </transition>

                  <div class="ps-tabs">
                    <v-icon size="20" :color="screen === 0 ? '#E0C27A' : 'rgba(255,255,255,.4)'">mdi-home</v-icon>
                    <v-icon size="20" :color="screen === 1 ? '#E0C27A' : 'rgba(255,255,255,.4)'">mdi-ticket-confirmation</v-icon>
                    <v-icon size="20" :color="screen === 2 ? '#E0C27A' : 'rgba(255,255,255,.4)'">mdi-food-apple</v-icon>
                    <v-icon size="20" :color="screen === 3 ? '#E0C27A' : 'rgba(255,255,255,.4)'">mdi-domain</v-icon>
                  </div>
                </div>
              </div>

              <div class="screen-dots">
                <button v-for="n in 4" :key="'dot' + n" :class="{ on: screen === n - 1 }" :aria-label="'Show screen ' + n" @click="setScreen(n - 1)"></button>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <button class="scroll-cue" aria-label="Scroll down" @click="scrollTo('roles')"><span></span></button>
    </section>

    <!-- ═════════════════════════ MARQUEE ═════════════════════════ -->
    <section class="marquee" aria-label="What's included">
      <div class="marquee-row">
        <div class="marquee-track">
          <span v-for="(m, i) in marqueeA.concat(marqueeA)" :key="'ma' + i" class="mq-chip"><v-icon size="16" color="#E0C27A">{{ m.i }}</v-icon>{{ m.t }}</span>
        </div>
      </div>
      <div class="marquee-row reverse">
        <div class="marquee-track">
          <span v-for="(m, i) in marqueeB.concat(marqueeB)" :key="'mb' + i" class="mq-chip"><v-icon size="16" color="#7FC8A0">{{ m.i }}</v-icon>{{ m.t }}</span>
        </div>
      </div>
    </section>

    <!-- ═════════════════════════ SEE IT AS… ═════════════════════════ -->
    <section id="roles" class="roles">
      <v-container>
        <div class="sec-head" v-reveal>
          <div class="badge"><v-icon small color="#E0C27A">mdi-account-switch</v-icon> SEE IT AS…</div>
          <h2 class="sec-title">One app. Six different experiences.</h2>
          <p class="sec-sub">Everyone signs in to the same app and sees exactly what they need — nothing they don't.</p>
        </div>

        <div class="role-tabs" v-reveal role="tablist">
          <button
            v-for="(r, i) in roles"
            :key="'rt' + i"
            class="role-tab"
            :class="{ active: activeRole === i }"
            role="tab"
            :aria-selected="activeRole === i ? 'true' : 'false'"
            @click="pickRole(i)"
          >
            <v-icon size="18">{{ r.icon }}</v-icon>
            <span>{{ r.label }}</span>
            <i v-if="activeRole === i && !rolePaused" class="role-progress" :key="'p' + roleTick"></i>
          </button>
        </div>

        <div class="role-panel" v-reveal>
          <transition name="role" mode="out-in">
            <v-row :key="activeRole" align="center">
              <v-col cols="12" md="6">
                <div class="role-copy">
                  <div class="role-ic" :style="{ background: roles[activeRole].color }">
                    <v-icon size="26" color="white">{{ roles[activeRole].icon }}</v-icon>
                  </div>
                  <h3>{{ roles[activeRole].headline }}</h3>
                  <p>{{ roles[activeRole].text }}</p>
                  <ul>
                    <li v-for="(p, j) in roles[activeRole].points" :key="'rp' + j" :style="{ animationDelay: (0.08 * j + 0.1) + 's' }">
                      <v-icon size="18" color="#7FC8A0">mdi-check-circle</v-icon>{{ p }}
                    </li>
                  </ul>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="role-visual">
                  <div class="rv-window">
                    <div class="rv-bar"><i></i><i></i><i></i><span>{{ roles[activeRole].label }} view</span></div>
                    <div class="rv-body">
                      <div
                        v-for="(c, j) in roles[activeRole].cards"
                        :key="'rc' + j"
                        class="rv-card"
                        :style="{ animationDelay: (0.1 * j) + 's' }"
                      >
                        <div class="rv-ic" :style="{ background: c.color }"><v-icon size="18" color="white">{{ c.icon }}</v-icon></div>
                        <div class="rv-txt"><b>{{ c.title }}</b><small>{{ c.sub }}</small></div>
                        <span v-if="c.tag" class="rv-tag">{{ c.tag }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </transition>
        </div>
      </v-container>
    </section>

    <!-- ═════════════════════════ FEATURES ═════════════════════════ -->
    <section id="features" class="features">
      <v-container>
        <div class="sec-head" v-reveal>
          <div class="badge"><v-icon small color="#E0C27A">mdi-star-four-points</v-icon> EVERYTHING INCLUDED</div>
          <h2 class="sec-title">Replace the pile of apps, flyers and email chains</h2>
          <p class="sec-sub">Every feature comes with every plan. No modules, no upsells.</p>
        </div>

        <div class="feat-grid">
          <div
            v-for="(f, i) in features"
            :key="'f' + i"
            v-reveal="{ delay: (i % 3) * 90 }"
            class="feat-card"
            @mousemove="spotlight"
          >
            <div class="feat-ic" :style="{ background: f.color }"><v-icon size="24" color="white">{{ f.icon }}</v-icon></div>
            <h3>{{ f.title }}</h3>
            <p>{{ f.text }}</p>
          </div>
        </div>
      </v-container>
    </section>

    <!-- ═════════════════════════ DISTRICTS ═════════════════════════ -->
    <section class="district">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="5">
            <div v-reveal>
              <div class="badge"><v-icon small color="#E0C27A">mdi-domain</v-icon> BUILT FOR DISTRICTS</div>
              <h2 class="sec-title left">Every school, one directory</h2>
              <p class="sec-sub left">
                Superintendents and district staff see every school at a glance and tap into any one of
                them. Each school keeps its own menus, calendar, payments and announcements — and
                families with kids at two schools see both in one app.
              </p>
              <ul class="check-list">
                <li><v-icon size="18" color="#7FC8A0">mdi-check-circle</v-icon>Add a new school in minutes</li>
                <li><v-icon size="18" color="#7FC8A0">mdi-check-circle</v-icon>District-wide or single-school announcements</li>
                <li><v-icon size="18" color="#7FC8A0">mdi-check-circle</v-icon>Admins choose what each role — and each person — can do</li>
                <li><v-icon size="18" color="#7FC8A0">mdi-check-circle</v-icon>Imports from your student information system (OneRoster)</li>
              </ul>
            </div>
          </v-col>
          <v-col cols="12" md="7">
            <div class="net" v-reveal>
              <svg viewBox="0 0 600 380" class="net-svg" aria-hidden="true">
                <defs>
                  <linearGradient id="lineG" x1="0" x2="1">
                    <stop offset="0" stop-color="#E0C27A" stop-opacity=".9" />
                    <stop offset="1" stop-color="#4F8F6F" stop-opacity=".6" />
                  </linearGradient>
                </defs>
                <path v-for="(s, i) in netSchools" :key="'l' + i" :id="'np' + i" class="net-line" :d="`M300 86 C 300 180, ${s.x} 170, ${s.x} ${s.y - 28}`" :style="{ animationDelay: (0.25 + i * 0.15) + 's' }" />
                <circle v-for="(s, i) in netSchools" :key="'pulse' + i" r="4" class="net-pulse">
                  <animateMotion :dur="(2.4 + i * 0.3) + 's'" repeatCount="indefinite" :begin="(1.2 + i * 0.4) + 's'">
                    <mpath :href="'#np' + i" />
                  </animateMotion>
                </circle>
              </svg>
              <div class="net-node hq">
                <v-icon size="22" color="#1A1408">mdi-bank</v-icon>
                <div><b>District Office</b><small>Riverside Unified</small></div>
              </div>
              <div
                v-for="(s, i) in netSchools"
                :key="'n' + i"
                class="net-node"
                :style="{ left: (s.x / 6) + '%', top: (s.y / 3.8) + '%', animationDelay: (0.6 + i * 0.15) + 's' }"
              >
                <div class="nn-ic" :style="{ background: s.c }"><v-icon size="16" color="white">{{ s.i }}</v-icon></div>
                <b>{{ s.n }}</b>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ═════════════════════════ FAMILY PAYMENTS ═════════════════════════ -->
    <section class="pay">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6" order="2" order-md="1">
            <div class="checkout" v-reveal>
              <div class="co-head"><v-icon size="18" color="#E0C27A">mdi-cart</v-icon> Checkout · Oakridge High</div>
              <div v-for="(l, i) in checkoutLines" :key="'cl' + i" class="co-line" :style="{ animationDelay: (0.2 + i * 0.25) + 's' }">
                <span>{{ l.t }}</span><b>{{ l.a }}</b>
              </div>
              <div class="co-line muted" :style="{ animationDelay: '1s' }"><span>Convenience fee</span><b>$2.50</b></div>
              <div class="co-total"><span>Total</span><b>$67.50</b></div>
              <div class="co-btn"><v-icon size="18" color="#1A1408">mdi-lock</v-icon> Pay with card</div>
              <div class="co-paid"><v-icon size="22" color="white">mdi-check-bold</v-icon> Paid — receipt emailed</div>
            </div>
          </v-col>
          <v-col cols="12" md="6" order="1" order-md="2">
            <div v-reveal>
              <div class="badge"><v-icon small color="#E0C27A">mdi-credit-card-check</v-icon> FAMILY PAYMENTS</div>
              <h2 class="sec-title left">Families pay the school — right in the app</h2>
              <p class="sec-sub left">
                Lunch top-ups, fees, the school store, field trips, game tickets, sports and
                before/after care. Money goes straight into your school's own Stripe account, and
                card details are handled by Stripe — never stored by us.
              </p>
              <div class="fee-note">
                <v-icon size="20" color="#E0C27A">mdi-information-outline</v-icon>
                <div>
                  Each checkout has a small convenience fee — <b>$1.50</b> under $50, <b>$2.50</b> at $50
                  or more. Your school decides whether families see it or the school covers it.
                  Families on free or reduced-price meals are never charged it.
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ═════════════════════════ NUMBERS ═════════════════════════ -->
    <section class="numbers">
      <v-container>
        <div class="num-grid">
          <div v-for="(n, i) in numbers" :key="'num' + i" v-count="n" v-reveal="{ delay: i * 80 }" class="num">
            <div class="num-v"><span class="num-val">{{ n.prefix || '' }}0{{ n.suffix || '' }}</span></div>
            <div class="num-l">{{ n.label }}</div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- ═════════════════════════ PRICING TEASER ═════════════════════════ -->
    <section id="pricing" class="pricing">
      <v-container>
        <div class="pt" v-reveal>
          <div class="pt-copy">
            <div class="badge"><v-icon small color="#E0C27A">mdi-tag-heart</v-icon> SIMPLE PRICING</div>
            <h2 class="sec-title left">From <span class="pt-price">$299</span> per school, per month</h2>
            <p class="sec-sub left">Everything included, unlimited users, free setup. Pay yearly for two months free — and districts with 5 or more schools save 10% on every school.</p>
            <div class="pt-ctas">
              <v-btn x-large depressed class="btn-gold btn-shine" to="/pricing">
                <v-icon left size="20" color="#1A1408">mdi-calculator-variant</v-icon>
                See pricing &amp; calculator
              </v-btn>
            </div>
          </div>
          <div class="pt-steps">
            <div v-for="(t, i) in teaserTiers" :key="t.n" class="pt-step" :style="{ transitionDelay: (i * 0.12) + 's' }">
              <div class="pt-n">{{ t.n }}</div>
              <div class="pt-v">{{ t.v }}</div>
              <div class="pt-s">{{ t.s }}</div>
            </div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- ═════════════════════════ TRUST ═════════════════════════ -->
    <section class="trust">
      <v-container>
        <div class="sec-head" v-reveal>
          <div class="badge"><v-icon small color="#E0C27A">mdi-shield-lock</v-icon> PRIVACY &amp; SAFETY</div>
          <h2 class="sec-title">Built with student privacy in mind</h2>
        </div>
        <div class="trust-grid">
          <div v-for="(t, i) in trust" :key="'tr' + i" v-reveal="{ delay: i * 80 }" class="trust-card" @mousemove="spotlight">
            <v-icon size="26" color="#E0C27A">{{ t.icon }}</v-icon>
            <h4>{{ t.title }}</h4>
            <p>{{ t.text }}</p>
          </div>
        </div>
      </v-container>
    </section>

    <!-- ═════════════════════════ SISTER COMPANY ═════════════════════════ -->
    <BusTrackBand />

    <!-- ═════════════════════════ FINAL CTA ═════════════════════════ -->
    <section class="final">
      <div class="final-bg" aria-hidden="true"><div class="orb orb-a"></div><div class="orb orb-b"></div></div>
      <v-container class="final-inner" v-reveal>
        <h2>Ready to bring your whole school together?</h2>
        <p>Fifteen minutes is usually enough to know if Schoolz Connect is right for you.</p>
        <v-btn x-large depressed class="btn-gold btn-shine" to="/demo">
          <v-icon left size="20" color="#1A1408">mdi-rocket-launch</v-icon>
          Book your demo
        </v-btn>
      </v-container>
    </section>

    <AppFooter />
  </div>
</template>

<script>
  import AppNavigation from '@/components/AppNavigation.vue'
  import AppFooter from '@/components/AppFooter.vue'
  import BusTrackBand from '@/components/BusTrackBand.vue'

  import { reduceMotion } from '@/plugins/motion'

  // Deterministic QR-looking pattern for the ticket screen.
  function qrPattern() {
    const N = 15, cells = []
    let seed = 7
    const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280 }
    const finder = (r, c) => {
      const inBox = (r0, c0) => r >= r0 && r < r0 + 5 && c >= c0 && c < c0 + 5
      for (const [r0, c0] of [[0, 0], [0, N - 5], [N - 5, 0]]) {
        if (inBox(r0, c0)) {
          const rr = r - r0, cc = c - c0
          return rr === 0 || rr === 4 || cc === 0 || cc === 4 || (rr === 2 && cc === 2)
        }
      }
      return null
    }
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const f = finder(r, c)
      cells.push(f === null ? rnd() > 0.52 : f)
    }
    return cells
  }

  export default {
    name: 'SchoolzHome',
    components: { AppNavigation, AppFooter, BusTrackBand },

    data: () => ({
      // hero
      screen: 0,
      roleWords: ['parent.', 'teacher.', 'student.', 'coach.', 'school.', 'district.'],
      roleWordIdx: 0,
      toasts: [
        { icon: 'mdi-message-text', color: '#4facfe', title: 'New message', sub: 'Ms. Rivera · Biology' },
        { icon: 'mdi-check-circle', color: '#4F8F6F', title: 'Payment received', sub: 'Lunch top-up · $25.00' },
        { icon: 'mdi-trophy', color: '#C9A24B', title: 'Final: Oakridge 28 – 21', sub: 'Varsity Football' },
      ],
      toastOn: [false, false, false],
      qrCells: qrPattern(),
      miniSchools: [
        { n: 'Oakridge High School', k: '1,140 students · High', i: 'mdi-school', c: '#4facfe' },
        { n: 'Maple Middle School', k: '620 students · Middle', i: 'mdi-book-open-variant', c: '#f7971e' },
        { n: 'Cedar Elementary', k: '410 students · Elementary', i: 'mdi-pencil', c: '#4F8F6F' },
        { n: 'Willow Elementary', k: '385 students · Elementary', i: 'mdi-palette', c: '#9B3D5E' },
      ],
      mouse: { x: 0, y: 0 },

      marqueeA: [
        { i: 'mdi-message-text', t: 'Messages' }, { i: 'mdi-bullhorn', t: 'Announcements' },
        { i: 'mdi-google-classroom', t: 'Classrooms' }, { i: 'mdi-book-open-page-variant', t: 'Homework & grades' },
        { i: 'mdi-calendar', t: 'Calendar' }, { i: 'mdi-food-apple', t: 'Lunch menus' },
        { i: 'mdi-wallet', t: 'Lunch balances' }, { i: 'mdi-ticket-confirmation', t: 'Event tickets' },
        { i: 'mdi-basketball', t: 'Sports' }, { i: 'mdi-medal', t: 'Recognition' },
      ],
      marqueeB: [
        { i: 'mdi-credit-card-outline', t: 'Fees & school store' }, { i: 'mdi-bus-school', t: 'Field trips' },
        { i: 'mdi-draw', t: 'Permission slips' }, { i: 'mdi-account-child', t: 'Before & after care' },
        { i: 'mdi-calendar-account', t: 'Conference sign-ups' }, { i: 'mdi-clipboard-account', t: 'Attendance alerts' },
        { i: 'mdi-medical-bag', t: 'Student records' }, { i: 'mdi-translate', t: '23 languages' },
        { i: 'mdi-image-multiple', t: 'Campus feed' }, { i: 'mdi-domain', t: 'District directory' },
      ],

      // roles
      activeRole: 0,
      rolePaused: false,
      roleTick: 0,
      roles: [
        {
          label: 'Parent', icon: 'mdi-account-heart', color: 'linear-gradient(135deg,#C9A24B,#E0C27A)',
          headline: 'Every child, every school, one home screen',
          text: 'Parents see each child\'s classes, homework, grades and notices — and handle the paperwork and payments that used to come home in a backpack.',
          points: ['Pay lunch, fees, trips, tickets and care', 'Sign permission slips on their phone', 'Report an absence and get attendance alerts', 'Book parent-teacher conferences'],
          cards: [
            { icon: 'mdi-wallet', color: '#C9A24B', title: 'Lunch balance · $24.50', sub: 'Low-balance alert on', tag: 'Top up' },
            { icon: 'mdi-book-open-page-variant', color: '#4facfe', title: 'Maya · 2 assignments due', sub: 'Biology, Algebra II' },
            { icon: 'mdi-draw', color: '#9B3D5E', title: 'Museum trip slip', sub: 'Sign & pay · due Thursday', tag: 'Sign' },
            { icon: 'mdi-calendar-account', color: '#4F8F6F', title: 'Conference sign-ups open', sub: 'Pick a time with Ms. Rivera' },
          ],
        },
        {
          label: 'Teacher', icon: 'mdi-account-tie', color: 'linear-gradient(135deg,#1B3A5C,#2A5A8C)',
          headline: 'Run your classes without the busywork',
          text: 'Post homework and notices, grade submissions, take attendance and message families — from one place.',
          points: ['Homework, submissions and grades', 'Class notices that reach students and parents', 'Attendance that alerts parents right away', 'Message families directly'],
          cards: [
            { icon: 'mdi-clipboard-check', color: '#4facfe', title: 'Period 3 · Biology', sub: '24 students · take attendance', tag: 'Open' },
            { icon: 'mdi-tray-full', color: '#C9A24B', title: '18 new submissions', sub: 'Lab report #4' },
            { icon: 'mdi-bullhorn', color: '#4F8F6F', title: 'Notice posted', sub: 'Seen by students and parents' },
            { icon: 'mdi-message-text', color: '#6E5A8C', title: '3 messages from parents', sub: 'Reply anytime' },
          ],
        },
        {
          label: 'Student', icon: 'mdi-account-school', color: 'linear-gradient(135deg,#4facfe,#2A7F9E)',
          headline: 'School life, all in one place',
          text: 'Classes, homework, the lunch menu, game days and what\'s happening on campus — on the phone they already carry.',
          points: ['Classes, timetable and what\'s due', 'Campus feed, groups and clubs', 'Lunch menu and sports schedule', 'Recognition when they shine'],
          cards: [
            { icon: 'mdi-timetable', color: '#4facfe', title: 'Next: Algebra II · Rm 204', sub: 'Starts in 10 minutes' },
            { icon: 'mdi-food-apple', color: '#f7971e', title: 'Today: chicken tacos', sub: 'Lunch menu' },
            { icon: 'mdi-basketball', color: '#9B3D5E', title: 'Game tonight · 7 PM', sub: 'vs. Lincoln · home' },
            { icon: 'mdi-medal', color: '#C9A24B', title: 'You were recognized!', sub: 'Student of the Month' },
          ],
        },
        {
          label: 'Coach', icon: 'mdi-whistle', color: 'linear-gradient(135deg,#9B3D5E,#C25A7C)',
          headline: 'Your team, your schedule, your scores',
          text: 'Coaches post games and results, keep the season record and see who\'s cleared to play.',
          points: ['Game schedule and results', 'Season record at a glance', 'Sports registration and physicals', 'Message players and families'],
          cards: [
            { icon: 'mdi-scoreboard', color: '#9B3D5E', title: 'Season record · 7–2', sub: 'Varsity Basketball' },
            { icon: 'mdi-calendar-plus', color: '#4facfe', title: 'Add game result', sub: 'vs. Lincoln · Friday', tag: 'Add' },
            { icon: 'mdi-file-check', color: '#4F8F6F', title: '22 of 24 physicals approved', sub: '2 waiting for review' },
            { icon: 'mdi-account-group', color: '#C9A24B', title: 'Team roster', sub: '24 players' },
          ],
        },
        {
          label: 'Cafeteria', icon: 'mdi-chef-hat', color: 'linear-gradient(135deg,#f7971e,#D97748)',
          headline: 'Plan the week\'s menus in minutes',
          text: 'Cafeteria staff publish breakfast and lunch for the week, flag allergens and keep lunch accounts in order.',
          points: ['Weekly breakfast and lunch menus', 'Allergens on every item', 'Lunch balances and low-balance alerts', 'Families see it instantly'],
          cards: [
            { icon: 'mdi-calendar-week', color: '#f7971e', title: 'Week of Oct 26', sub: '5 of 5 days published' },
            { icon: 'mdi-alert-circle', color: '#B5473B', title: 'Allergens flagged', sub: 'Gluten · Dairy · Nuts' },
            { icon: 'mdi-wallet', color: '#4F8F6F', title: 'Lunch accounts', sub: '12 low balances alerted' },
            { icon: 'mdi-food', color: '#C9A24B', title: 'Tuesday lunch', sub: 'Chicken tacos · bean salad' },
          ],
        },
        {
          label: 'Admin', icon: 'mdi-shield-account', color: 'linear-gradient(135deg,#4F8F6F,#6FB08F)',
          headline: 'The front office, finally in one place',
          text: 'Admins run announcements, the calendar, payments and records — and decide exactly what each role and each person can do.',
          points: ['Announcements to the whole school or a group', 'Payments, refunds and money reports', 'Student records, nurse log and front desk', 'Permissions per role and per person'],
          cards: [
            { icon: 'mdi-bullhorn', color: '#4F8F6F', title: 'Announcement scheduled', sub: 'All families · tomorrow 7 AM' },
            { icon: 'mdi-cash-multiple', color: '#C9A24B', title: 'This week · $4,280 collected', sub: 'Lunch, fees and tickets' },
            { icon: 'mdi-account-key', color: '#6E5A8C', title: 'Permissions', sub: 'Coaches can scan tickets', tag: 'Edit' },
            { icon: 'mdi-account-clock', color: '#4facfe', title: '6 absences reported', sub: 'by parents this morning' },
          ],
        },
      ],

      features: [
        { icon: 'mdi-message-text', color: 'linear-gradient(135deg,#6E5A8C,#4F3F68)', title: 'Messages', text: 'Direct messages between families, teachers, coaches and staff — with photos and files.' },
        { icon: 'mdi-bullhorn', color: 'linear-gradient(135deg,#4F8F6F,#386B52)', title: 'Announcements & alerts', text: 'Reach the whole school, a grade or a group, with push notifications and email.' },
        { icon: 'mdi-google-classroom', color: 'linear-gradient(135deg,#1B3A5C,#2A5A8C)', title: 'Classrooms', text: 'Homework, submissions, grades, materials and notices for every class.' },
        { icon: 'mdi-credit-card-check', color: 'linear-gradient(135deg,#C9A24B,#9C7A34)', title: 'Family payments', text: 'Fees, the school store, field trips and refunds — paid straight to your school.' },
        { icon: 'mdi-food-apple', color: 'linear-gradient(135deg,#f7971e,#D97748)', title: 'Lunch', text: 'Weekly menus with allergens, plus lunch balances and low-balance alerts.' },
        { icon: 'mdi-ticket-confirmation', color: 'linear-gradient(135deg,#9B3D5E,#742C46)', title: 'Event tickets', text: 'Sell tickets for games and events, with QR codes scanned at the door.' },
        { icon: 'mdi-basketball', color: 'linear-gradient(135deg,#2A7F9E,#1B5C74)', title: 'Sports', text: 'Schedules, results, season records, registration and physicals.' },
        { icon: 'mdi-account-child', color: 'linear-gradient(135deg,#4facfe,#2A7F9E)', title: 'Before & after care', text: 'Enrollment and monthly billing for care programs.' },
        { icon: 'mdi-draw', color: 'linear-gradient(135deg,#B5473B,#8F3128)', title: 'Permission slips', text: 'Parents sign on their phone — and pay for the trip in the same step.' },
        { icon: 'mdi-clipboard-account', color: 'linear-gradient(135deg,#386B52,#4F8F6F)', title: 'Attendance', text: 'Parents report absences ahead of time and get alerts when a child is marked out.' },
        { icon: 'mdi-medical-bag', color: 'linear-gradient(135deg,#795548,#5D4037)', title: 'Student records', text: 'Guardians, pickup list, health info and accommodations — only for the staff you allow.' },
        { icon: 'mdi-translate', color: 'linear-gradient(135deg,#607D8B,#455A64)', title: '23 languages', text: 'Families choose their language and read announcements and messages in it.' },
      ],

      netSchools: [
        { n: 'Oakridge High', x: 70, y: 300, i: 'mdi-school', c: '#4facfe' },
        { n: 'Maple Middle', x: 200, y: 330, i: 'mdi-book-open-variant', c: '#f7971e' },
        { n: 'Cedar Elem.', x: 400, y: 330, i: 'mdi-pencil', c: '#4F8F6F' },
        { n: 'Willow Elem.', x: 530, y: 300, i: 'mdi-palette', c: '#9B3D5E' },
      ],

      checkoutLines: [
        { t: 'Lunch top-up · Maya', a: '$25.00' },
        { t: 'Museum field trip · Leo', a: '$15.00' },
        { t: '2 × Homecoming tickets', a: '$10.00' },
        { t: 'Spirit hoodie · M', a: '$15.00' },
      ],

      numbers: [
        { value: 6, label: 'roles, one app' },
        { value: 299, prefix: '$', label: 'per school per month, everything included' },
        { value: 23, label: 'languages for families' },
        { value: 0, prefix: '$', label: 'setup or onboarding fee' },
      ],

      teaserTiers: [
        { n: '1–4 schools', v: '$299/mo', s: 'or $2,990/yr per school' },
        { n: '5–9 schools', v: '10% off', s: 'every school on the account' },
        { n: '10+ schools', v: 'Custom', s: 'district pricing' },
      ],

      trust: [
        { icon: 'mdi-account-key', title: 'Role-based access', text: 'Everyone sees only what their role allows, and admins can adjust it per role or per person.' },
        { icon: 'mdi-lock', title: 'Encrypted', text: 'Data is encrypted in transit and at rest on Google Cloud.' },
        { icon: 'mdi-credit-card-lock', title: 'Payments by Stripe', text: 'Card details go to Stripe, not to us — we never see or store them.' },
        { icon: 'mdi-eye-check', title: 'Moderation tools', text: 'Admins can review, pin and remove posts, groups and announcements.' },
      ],
    }),

    computed: {
      roleWord() { return this.roleWords[this.roleWordIdx] },
      tiltStyle() {
        if (this.reduced) return {}
        return { transform: `perspective(1200px) rotateY(${this.mouse.x * -6}deg) rotateX(${this.mouse.y * 5}deg)` }
      },
    },

    created() {
      this.reduced = reduceMotion()
      this.timers = []
    },

    mounted() {
      const every = (fn, ms) => this.timers.push(setInterval(fn, ms))
      every(() => { this.screen = (this.screen + 1) % 4 }, 3800)
      every(() => { this.roleWordIdx = (this.roleWordIdx + 1) % this.roleWords.length }, 2200)
      every(() => {
        if (this.rolePaused) return
        this.activeRole = (this.activeRole + 1) % this.roles.length
        this.roleTick++
      }, 6000)
      this.cycleToasts()
    },

    beforeDestroy() {
      this.timers.forEach(clearInterval)
      ;(this.timeouts || []).forEach(clearTimeout)
    },

    methods: {
      cycleToasts() {
        this.timeouts = []
        const show = (i, at) => this.timeouts.push(setTimeout(() => this.$set(this.toastOn, i, true), at))
        const hide = (i, at) => this.timeouts.push(setTimeout(() => this.$set(this.toastOn, i, false), at))
        show(0, 900); show(1, 2200); show(2, 3500)
        hide(0, 6800); hide(1, 7600); hide(2, 8400)
        this.timeouts.push(setTimeout(() => this.cycleToasts(), 9400))
      },
      setScreen(i) { this.screen = i },
      pickRole(i) { this.activeRole = i; this.rolePaused = true },
      scrollTo(id) {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'start' })
      },
      onMouse(e) {
        if (this.reduced || window.innerWidth < 960) return
        this.mouse = { x: e.clientX / window.innerWidth - 0.5, y: e.clientY / window.innerHeight - 0.5 }
      },
      spotlight(e) {
        const r = e.currentTarget.getBoundingClientRect()
        e.currentTarget.style.setProperty('--mx', (e.clientX - r.left) + 'px')
        e.currentTarget.style.setProperty('--my', (e.clientY - r.top) + 'px')
      },
      sparkStyle(n) {
        const left = (n * 53) % 100, top = (n * 37) % 100
        return { left: left + '%', top: top + '%', animationDelay: ((n * 0.7) % 6) + 's', animationDuration: (5 + (n % 5)) + 's' }
      },
    },
  }
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap');

  .sz-home {
    --bg: #0B1424;
    --bg-alt: #0F1B2E;
    --card: #121F35;
    --card-2: #172A47;
    --line: rgba(255, 255, 255, 0.09);
    --gold: #C9A24B;
    --gold-l: #E0C27A;
    --gold-d: #9C7A34;
    --green: #7FC8A0;
    --text: #F3F5F8;
    --text-2: rgba(243, 245, 248, 0.7);
    --text-3: rgba(243, 245, 248, 0.5);
    --ease: cubic-bezier(0.22, 1, 0.36, 1);

    background: var(--bg);
    color: var(--text);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    overflow-x: hidden;
    padding-top: 80px;
  }
  .sz-home *, .sz-home *::before, .sz-home *::after { box-sizing: border-box; }
  .sz-home h1, .sz-home h2, .sz-home h3, .sz-home h4 { font-family: 'Space Grotesk', 'Inter', sans-serif; letter-spacing: -0.02em; }

  /* ── reveal ───────────────────────────── */
  .rv { opacity: 0; transform: translateY(28px); transition: opacity 0.8s var(--ease), transform 0.8s var(--ease); }
  .rv.is-in { opacity: 1; transform: none; }

  /* ── buttons ──────────────────────────── */
  .btn-gold {
    text-transform: none !important; font-weight: 700 !important; letter-spacing: 0.01em; font-size: 15px;
    border-radius: 12px !important; color: #1A1408 !important;
    background: linear-gradient(150deg, var(--gold-l) 0%, var(--gold) 60%, var(--gold-d) 100%) !important;
    box-shadow: 0 8px 24px rgba(201, 162, 75, 0.3) !important;
    transition: transform 0.25s var(--ease), box-shadow 0.25s var(--ease) !important;
  }
  .btn-gold:hover { transform: translateY(-2px); box-shadow: 0 14px 34px rgba(201, 162, 75, 0.45) !important; }
  .btn-gold:active { transform: translateY(0) scale(0.98); }
  .btn-shine { position: relative; overflow: hidden; }
  .btn-shine::after {
    content: ''; position: absolute; top: 0; left: -60%; width: 40%; height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.55), transparent);
    transform: skewX(-20deg); animation: shine 4.5s ease-in-out infinite;
  }
  @keyframes shine { 0%, 70% { left: -60%; } 100% { left: 130%; } }
  .btn-ghost { text-transform: none !important; color: var(--text) !important; font-weight: 600 !important; letter-spacing: 0; border-radius: 12px !important; }

  .badge {
    display: inline-flex; align-items: center; gap: 8px; padding: 7px 14px; border-radius: 999px;
    border: 1px solid rgba(201, 162, 75, 0.35); background: rgba(201, 162, 75, 0.08);
    color: var(--gold-l); font-size: 0.72rem; font-weight: 700; letter-spacing: 0.12em; margin-bottom: 18px;
  }
  .sec-head { text-align: center; max-width: 760px; margin: 0 auto 52px; }
  .sec-title { font-size: clamp(2rem, 4vw, 2.9rem); font-weight: 700; line-height: 1.12; margin: 0 0 14px; }
  .sec-sub { font-size: 1.08rem; line-height: 1.65; color: var(--text-2); margin: 0; }
  .sec-title.left, .sec-sub.left { text-align: left; }

  /* ═════════════════ HERO ═════════════════ */
  .hero { position: relative; min-height: calc(100vh - 80px); display: flex; align-items: center; overflow: hidden; padding: 40px 0 90px; }
  .hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(1200px 600px at 70% 10%, #1B3A5C 0%, transparent 60%), linear-gradient(170deg, #101D33 0%, #0B1424 70%); }
  .orb { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.5; will-change: transform; }
  .orb-a { width: 520px; height: 520px; background: radial-gradient(circle, rgba(201, 162, 75, 0.55), transparent 65%); top: -160px; right: -120px; animation: drift-a 18s ease-in-out infinite; }
  .orb-b { width: 460px; height: 460px; background: radial-gradient(circle, rgba(79, 172, 254, 0.35), transparent 65%); bottom: -200px; left: -140px; animation: drift-b 22s ease-in-out infinite; }
  .orb-c { width: 340px; height: 340px; background: radial-gradient(circle, rgba(127, 200, 160, 0.3), transparent 65%); top: 30%; left: 40%; animation: drift-c 26s ease-in-out infinite; }
  @keyframes drift-a { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(-80px, 60px) scale(1.12); } }
  @keyframes drift-b { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(90px, -50px) scale(1.1); } }
  @keyframes drift-c { 0%, 100% { transform: translate(0, 0); } 33% { transform: translate(60px, -40px); } 66% { transform: translate(-50px, 30px); } }
  .hero-grid {
    position: absolute; inset: 0; opacity: 0.35;
    background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 56px 56px;
    mask-image: radial-gradient(ellipse at 60% 40%, #000 20%, transparent 70%);
    -webkit-mask-image: radial-gradient(ellipse at 60% 40%, #000 20%, transparent 70%);
    animation: grid-move 30s linear infinite;
  }
  @keyframes grid-move { to { background-position: 56px 56px; } }
  .spark { position: absolute; width: 3px; height: 3px; border-radius: 50%; background: var(--gold-l); opacity: 0; animation: twinkle 6s ease-in-out infinite; }
  @keyframes twinkle { 0%, 100% { opacity: 0; transform: translateY(0); } 50% { opacity: 0.8; transform: translateY(-14px); } }

  .hero-inner { position: relative; z-index: 2; }
  .hero-copy > * { opacity: 0; animation: rise 0.9s var(--ease) forwards; }
  .in-1 { animation-delay: 0.05s !important; } .in-2 { animation-delay: 0.18s !important; } .in-3 { animation-delay: 0.32s !important; }
  .in-4 { animation-delay: 0.46s !important; } .in-5 { animation-delay: 0.6s !important; }
  @keyframes rise { from { opacity: 0; transform: translateY(26px); } to { opacity: 1; transform: none; } }

  .eyebrow { display: inline-flex; align-items: center; gap: 10px; padding: 8px 16px; border-radius: 999px; border: 1px solid rgba(255, 255, 255, 0.16); background: rgba(255, 255, 255, 0.04); font-size: 0.82rem; font-weight: 600; color: rgba(255, 255, 255, 0.85); margin-bottom: 26px; backdrop-filter: blur(6px); }
  .live-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--green); box-shadow: 0 0 0 0 rgba(127, 200, 160, 0.7); animation: ping 2s infinite; }
  @keyframes ping { 0% { box-shadow: 0 0 0 0 rgba(127, 200, 160, 0.7); } 70% { box-shadow: 0 0 0 10px rgba(127, 200, 160, 0); } 100% { box-shadow: 0 0 0 0 rgba(127, 200, 160, 0); } }

  .hero-title { font-size: clamp(2.4rem, 5.2vw, 4rem); font-weight: 700; line-height: 1.05; margin: 0 0 24px; }
  .shimmer {
    display: inline-block;
    background: linear-gradient(100deg, var(--gold-d) 0%, var(--gold-l) 30%, #FFF3D1 50%, var(--gold-l) 70%, var(--gold-d) 100%);
    background-size: 220% 100%; -webkit-background-clip: text; background-clip: text; color: transparent;
    animation: shimmer 5s linear infinite;
  }
  @keyframes shimmer { to { background-position: -220% 0; } }
  .hero-sub { font-size: 1.15rem; line-height: 1.7; color: var(--text-2); max-width: 560px; margin: 0 0 32px; }
  .rotator { display: inline-block; min-width: 7.5ch; font-weight: 700; color: var(--gold-l); }
  .rotator-word { display: inline-block; }
  .word-enter-active, .word-leave-active { transition: all 0.35s var(--ease); }
  .word-enter { opacity: 0; transform: translateY(60%); }
  .word-leave-to { opacity: 0; transform: translateY(-60%); }

  .hero-ctas { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 28px; }
  .hero-ctas .btn-gold { padding: 0 30px !important; height: 56px !important; }
  .hero-trust { display: flex; flex-wrap: wrap; gap: 18px; font-size: 0.9rem; color: var(--text-2); }
  .hero-trust span { display: inline-flex; align-items: center; gap: 6px; }

  .hero-stage-col { display: flex; justify-content: center; }
  .hero-stage { position: relative; width: 300px; padding: 30px 0 10px; transition: transform 0.4s var(--ease); transform-style: preserve-3d; opacity: 0; animation: rise 1s var(--ease) 0.35s forwards; }
  .stage-glow { position: absolute; inset: 40px -40px 30px; background: radial-gradient(circle, rgba(201, 162, 75, 0.35), transparent 65%); filter: blur(40px); animation: breathe 6s ease-in-out infinite; }
  @keyframes breathe { 0%, 100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 1; transform: scale(1.08); } }

  .phone { position: relative; width: 300px; height: 610px; border-radius: 46px; padding: 12px; background: linear-gradient(160deg, #2B3A55, #121C2E); box-shadow: 0 40px 80px rgba(0, 0, 0, 0.55), inset 0 0 0 2px rgba(255, 255, 255, 0.08); animation: float 7s ease-in-out infinite; }
  @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }
  .phone-notch { position: absolute; top: 22px; left: 50%; transform: translateX(-50%); width: 96px; height: 26px; border-radius: 20px; background: #05080F; z-index: 3; }
  .phone-screen { position: relative; height: 100%; border-radius: 36px; overflow: hidden; background: linear-gradient(180deg, #0F1B2E, #0B1424); display: flex; flex-direction: column; }
  .ps-status { display: flex; justify-content: space-between; padding: 14px 24px 0; font-size: 12px; font-weight: 600; height: 44px; }
  .ps-body { flex: 1; padding: 14px 16px 0; overflow: hidden; display: flex; flex-direction: column; gap: 10px; }
  .ps-hello { font-family: 'Space Grotesk', sans-serif; font-size: 19px; font-weight: 700; }
  .ps-title { font-family: 'Space Grotesk', sans-serif; font-size: 20px; font-weight: 700; }
  .ps-sub { font-size: 11.5px; color: var(--text-3); margin-top: -6px; }
  .ps-kids { display: flex; gap: 8px; }
  .ps-kid { flex: 1; display: flex; gap: 8px; align-items: center; padding: 8px; border-radius: 14px; background: rgba(255, 255, 255, 0.05); border: 1px solid var(--line); }
  .ps-kid b, .ps-card b, .ps-school b { display: block; font-size: 12.5px; }
  .ps-kid small, .ps-card small, .ps-school small { display: block; font-size: 10.5px; color: var(--text-3); }
  .kid-av { width: 30px; height: 30px; border-radius: 50%; display: grid; place-items: center; font-weight: 700; font-size: 13px; }
  .ps-card { display: flex; align-items: center; gap: 10px; padding: 11px 12px; border-radius: 16px; background: rgba(255, 255, 255, 0.05); border: 1px solid var(--line); animation: pop 0.5s var(--ease) both; }
  .ps-card:nth-child(4) { animation-delay: 0.1s; } .ps-card:nth-child(5) { animation-delay: 0.2s; } .ps-card:nth-child(6) { animation-delay: 0.3s; } .ps-card:nth-child(7) { animation-delay: 0.4s; }
  .ps-card > div { flex: 1; min-width: 0; }
  .ps-card.gold { background: linear-gradient(135deg, var(--gold-l), var(--gold)); color: #1A1408; border: 0; }
  .ps-card.gold small { color: rgba(26, 20, 8, 0.7); }
  .ps-amt { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 16px; }
  .ps-chip { font-size: 10.5px; font-weight: 700; padding: 4px 10px; border-radius: 999px; background: rgba(201, 162, 75, 0.2); color: var(--gold-l); }
  @keyframes pop { from { opacity: 0; transform: translateY(10px) scale(0.97); } to { opacity: 1; transform: none; } }

  .ticket { margin-top: 6px; border-radius: 22px; background: linear-gradient(160deg, #1E3556, #142640); border: 1px solid rgba(255, 255, 255, 0.1); overflow: hidden; animation: pop 0.5s var(--ease) both; }
  .ticket-top { padding: 16px 18px 12px; }
  .ticket-top small { color: var(--gold-l); font-weight: 700; font-size: 10.5px; letter-spacing: 0.08em; }
  .ticket-top b { display: block; font-family: 'Space Grotesk', sans-serif; font-size: 19px; margin-top: 4px; }
  .ticket-top span { font-size: 12px; color: var(--text-3); }
  .ticket-cut { height: 1px; margin: 0 14px; border-top: 2px dashed rgba(255, 255, 255, 0.15); }
  .qr { position: relative; margin: 16px auto; width: 165px; height: 165px; padding: 10px; border-radius: 14px; background: #fff; display: grid; grid-template-columns: repeat(15, 1fr); gap: 1px; overflow: hidden; }
  .qr i { background: transparent; border-radius: 1px; }
  .qr i.on { background: #0B1424; }
  .qr-scan { position: absolute; left: 0; right: 0; height: 30px; top: -30px; background: linear-gradient(180deg, transparent, rgba(201, 162, 75, 0.55), transparent); animation: scan 2.2s ease-in-out infinite; }
  @keyframes scan { 0% { top: -30px; } 100% { top: 165px; } }
  .ticket-foot { text-align: center; font-size: 11px; color: var(--text-3); padding: 0 0 16px; }

  .week { display: flex; gap: 5px; }
  .wd { flex: 1; text-align: center; padding: 8px 0 6px; border-radius: 10px; background: rgba(255, 255, 255, 0.04); font-size: 11px; font-weight: 700; color: var(--text-3); position: relative; }
  .wd i { display: block; width: 4px; height: 4px; margin: 4px auto 0; border-radius: 50%; background: #38f9d7; }
  .wd.sel { background: rgba(56, 249, 215, 0.16); color: #fff; box-shadow: inset 0 0 0 1px rgba(56, 249, 215, 0.4); }
  .meal { padding: 12px; border-radius: 16px; background: rgba(255, 255, 255, 0.05); border: 1px solid var(--line); animation: pop 0.5s var(--ease) both; }
  .meal + .meal { animation-delay: 0.12s; }
  .meal-h { display: flex; align-items: center; gap: 6px; font-weight: 700; font-size: 13px; margin-bottom: 6px; }
  .meal-i { display: flex; justify-content: space-between; gap: 6px; font-size: 12px; color: rgba(255, 255, 255, 0.82); padding: 5px 0; border-top: 1px solid rgba(255, 255, 255, 0.05); }
  .meal-i em { font-style: normal; font-size: 9.5px; font-weight: 700; color: #f7971e; background: rgba(247, 151, 30, 0.14); padding: 2px 6px; border-radius: 999px; white-space: nowrap; }

  .ps-school { display: flex; align-items: center; gap: 10px; padding: 11px 12px; border-radius: 16px; background: rgba(255, 255, 255, 0.05); border: 1px solid var(--line); animation: pop 0.5s var(--ease) both; }
  .ps-school > div:nth-child(2) { flex: 1; min-width: 0; }
  .sch-ic { width: 32px; height: 32px; border-radius: 10px; display: grid; place-items: center; }

  .ps-tabs { display: flex; justify-content: space-around; padding: 12px 10px 20px; border-top: 1px solid var(--line); background: rgba(11, 20, 36, 0.9); }

  .screen-enter-active, .screen-leave-active { transition: all 0.45s var(--ease); }
  .screen-enter { opacity: 0; transform: translateX(30px); }
  .screen-leave-to { opacity: 0; transform: translateX(-30px); }

  .screen-dots { display: flex; justify-content: center; gap: 8px; margin-top: 22px; }
  .screen-dots button { width: 8px; height: 8px; border-radius: 999px; border: 0; background: rgba(255, 255, 255, 0.25); cursor: pointer; transition: all 0.3s var(--ease); padding: 0; }
  .screen-dots button.on { width: 26px; background: var(--gold-l); }

  .float-toast { position: absolute; z-index: 5; display: flex; align-items: center; gap: 10px; padding: 10px 14px 10px 10px; border-radius: 16px; min-width: 200px; background: rgba(23, 38, 64, 0.82); border: 1px solid rgba(255, 255, 255, 0.12); backdrop-filter: blur(14px); box-shadow: 0 18px 40px rgba(0, 0, 0, 0.4); opacity: 0; transform: translateY(14px) scale(0.92); transition: all 0.6s var(--ease); pointer-events: none; }
  .float-toast.show { opacity: 1; transform: none; }
  .toast-0 { top: 40px; left: -210px; }
  .toast-1 { top: 300px; right: -160px; }
  .toast-2 { bottom: 110px; left: -200px; }
  .toast-ic { width: 32px; height: 32px; border-radius: 10px; display: grid; place-items: center; flex-shrink: 0; }
  .toast-t { font-size: 13px; font-weight: 700; }
  .toast-s { font-size: 11.5px; color: var(--text-3); }

  .scroll-cue { position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%); width: 26px; height: 42px; border-radius: 14px; border: 2px solid rgba(255, 255, 255, 0.3); background: transparent; cursor: pointer; z-index: 3; }
  .scroll-cue span { position: absolute; top: 7px; left: 50%; width: 4px; height: 8px; margin-left: -2px; border-radius: 2px; background: var(--gold-l); animation: cue 1.8s ease-in-out infinite; }
  @keyframes cue { 0% { opacity: 0; transform: translateY(0); } 40% { opacity: 1; } 100% { opacity: 0; transform: translateY(14px); } }

  /* ═════════════════ MARQUEE ═════════════════ */
  .marquee { padding: 28px 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); background: var(--bg-alt); overflow: hidden; display: flex; flex-direction: column; gap: 14px; mask-image: linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent); -webkit-mask-image: linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent); }
  .marquee-row { overflow: hidden; }
  .marquee-track { display: flex; gap: 12px; width: max-content; animation: marquee 48s linear infinite; }
  .marquee-row.reverse .marquee-track { animation-direction: reverse; animation-duration: 54s; }
  .marquee:hover .marquee-track { animation-play-state: paused; }
  @keyframes marquee { to { transform: translateX(-50%); } }
  .mq-chip { display: inline-flex; align-items: center; gap: 8px; white-space: nowrap; padding: 10px 18px; border-radius: 999px; background: rgba(255, 255, 255, 0.04); border: 1px solid var(--line); font-weight: 600; font-size: 0.92rem; color: var(--text-2); }

  /* ═════════════════ ROLES ═════════════════ */
  .roles { padding: 110px 0; }
  .role-tabs { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; margin-bottom: 40px; }
  .role-tab { position: relative; overflow: hidden; display: inline-flex; align-items: center; gap: 8px; padding: 12px 20px; border-radius: 14px; border: 1px solid var(--line); background: rgba(255, 255, 255, 0.03); color: var(--text-2); font-weight: 600; font-size: 0.95rem; cursor: pointer; transition: all 0.3s var(--ease); }
  .role-tab .v-icon { color: inherit !important; }
  .role-tab:hover { color: var(--text); border-color: rgba(201, 162, 75, 0.35); transform: translateY(-2px); }
  .role-tab.active { color: #1A1408; background: linear-gradient(150deg, var(--gold-l), var(--gold)); border-color: transparent; box-shadow: 0 10px 26px rgba(201, 162, 75, 0.3); }
  .role-progress { position: absolute; left: 0; bottom: 0; height: 3px; width: 100%; background: rgba(26, 20, 8, 0.4); transform-origin: left; animation: progress 6s linear forwards; }
  @keyframes progress { from { transform: scaleX(0); } to { transform: scaleX(1); } }

  .role-panel { min-height: 420px; }
  .role-enter-active, .role-leave-active { transition: all 0.45s var(--ease); }
  .role-enter { opacity: 0; transform: translateY(18px); }
  .role-leave-to { opacity: 0; transform: translateY(-12px); }
  .role-copy h3 { font-size: 1.9rem; font-weight: 700; line-height: 1.2; margin: 18px 0 12px; }
  .role-copy p { color: var(--text-2); line-height: 1.7; font-size: 1.05rem; margin-bottom: 20px; }
  .role-ic { width: 56px; height: 56px; border-radius: 16px; display: grid; place-items: center; box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3); }
  .role-copy ul, .check-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 12px; }
  .role-copy li, .check-list li { display: flex; align-items: center; gap: 10px; font-weight: 500; }
  .role-copy li { opacity: 0; animation: rise 0.6s var(--ease) forwards; }

  .role-visual { perspective: 1000px; }
  .rv-window { border-radius: 22px; background: var(--card); border: 1px solid var(--line); box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4); overflow: hidden; transform: rotateY(-6deg) rotateX(3deg); transition: transform 0.6s var(--ease); }
  .rv-window:hover { transform: none; }
  .rv-bar { display: flex; align-items: center; gap: 6px; padding: 12px 16px; border-bottom: 1px solid var(--line); background: rgba(255, 255, 255, 0.03); }
  .rv-bar i { width: 10px; height: 10px; border-radius: 50%; background: rgba(255, 255, 255, 0.15); }
  .rv-bar i:nth-child(1) { background: #ff5f57; } .rv-bar i:nth-child(2) { background: #febc2e; } .rv-bar i:nth-child(3) { background: #28c840; }
  .rv-bar span { margin-left: 10px; font-size: 12px; font-weight: 600; color: var(--text-3); }
  .rv-body { padding: 16px; display: grid; gap: 10px; }
  .rv-card { display: flex; align-items: center; gap: 12px; padding: 14px; border-radius: 16px; background: rgba(255, 255, 255, 0.04); border: 1px solid var(--line); opacity: 0; animation: slide-in 0.55s var(--ease) forwards; }
  @keyframes slide-in { from { opacity: 0; transform: translateX(24px); } to { opacity: 1; transform: none; } }
  .rv-ic { width: 38px; height: 38px; border-radius: 12px; display: grid; place-items: center; flex-shrink: 0; }
  .rv-txt { flex: 1; min-width: 0; }
  .rv-txt b { display: block; font-size: 0.95rem; }
  .rv-txt small { display: block; color: var(--text-3); font-size: 0.82rem; }
  .rv-tag { font-size: 0.75rem; font-weight: 700; padding: 5px 12px; border-radius: 999px; background: rgba(201, 162, 75, 0.16); color: var(--gold-l); }

  /* ═════════════════ FEATURES ═════════════════ */
  .features { padding: 100px 0; background: var(--bg-alt); }
  .feat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
  .feat-card { position: relative; padding: 26px; border-radius: 20px; background: var(--card); border: 1px solid var(--line); overflow: hidden; transition: transform 0.4s var(--ease), border-color 0.4s var(--ease), box-shadow 0.4s var(--ease), opacity 0.8s var(--ease); }
  .feat-card::before, .trust-card::before { content: ''; position: absolute; inset: 0; opacity: 0; transition: opacity 0.3s; background: radial-gradient(380px circle at var(--mx, 50%) var(--my, 50%), rgba(201, 162, 75, 0.14), transparent 45%); pointer-events: none; }
  .feat-card:hover, .trust-card:hover { transform: translateY(-6px); border-color: rgba(201, 162, 75, 0.35); box-shadow: 0 24px 50px rgba(0, 0, 0, 0.35); }
  .feat-card:hover::before, .trust-card:hover::before { opacity: 1; }
  .feat-ic { width: 50px; height: 50px; border-radius: 14px; display: grid; place-items: center; margin-bottom: 18px; transition: transform 0.4s var(--ease); }
  .feat-card:hover .feat-ic { transform: scale(1.1) rotate(-6deg); }
  .feat-card h3 { font-size: 1.2rem; font-weight: 700; margin: 0 0 8px; }
  .feat-card p { color: var(--text-2); line-height: 1.6; margin: 0; font-size: 0.96rem; }

  /* ═════════════════ DISTRICT ═════════════════ */
  .district { padding: 110px 0; }
  .district .check-list { margin-top: 26px; }
  .net { position: relative; width: 100%; aspect-ratio: 600 / 380; }
  .net-svg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
  .net-line { fill: none; stroke: url(#lineG); stroke-width: 2; stroke-dasharray: 400; stroke-dashoffset: 400; }
  .net.is-in .net-line { animation: draw 1.4s var(--ease) forwards; }
  @keyframes draw { to { stroke-dashoffset: 0; } }
  .net-pulse { fill: var(--gold-l); filter: drop-shadow(0 0 6px rgba(224, 194, 122, 0.9)); opacity: 0; }
  .net.is-in .net-pulse { opacity: 1; transition: opacity 0.4s 1.6s; }
  .net-node { position: absolute; transform: translate(-50%, -50%) scale(0.6); opacity: 0; display: flex; flex-direction: column; align-items: center; gap: 6px; text-align: center; font-size: 0.85rem; }
  .net.is-in .net-node { animation: node-in 0.6s var(--ease) forwards; }
  @keyframes node-in { to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
  .nn-ic { width: 46px; height: 46px; border-radius: 14px; display: grid; place-items: center; box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35), 0 0 0 6px rgba(255, 255, 255, 0.04); }
  .net-node.hq { left: 50%; top: 16%; flex-direction: row; gap: 12px; padding: 12px 18px; border-radius: 18px; background: linear-gradient(150deg, var(--gold-l), var(--gold)); color: #1A1408; text-align: left; box-shadow: 0 18px 40px rgba(201, 162, 75, 0.35); animation-delay: 0.1s; }
  .net-node.hq b { display: block; } .net-node.hq small { display: block; opacity: 0.75; }

  /* ═════════════════ PAYMENTS ═════════════════ */
  .pay { padding: 100px 0; background: var(--bg-alt); }
  .checkout { position: relative; max-width: 430px; margin: 0 auto; padding: 24px; border-radius: 24px; background: var(--card); border: 1px solid var(--line); box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4); overflow: hidden; }
  .co-head { display: flex; align-items: center; gap: 8px; font-weight: 700; margin-bottom: 14px; }
  .co-line { display: flex; justify-content: space-between; padding: 11px 0; border-bottom: 1px solid var(--line); font-size: 0.95rem; opacity: 0; }
  .co-line.muted { color: var(--text-3); }
  .checkout.is-in .co-line { animation: rise 0.5s var(--ease) forwards; }
  .co-total { display: flex; justify-content: space-between; padding: 16px 0; font-family: 'Space Grotesk', sans-serif; font-size: 1.3rem; font-weight: 700; }
  .co-btn { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px; border-radius: 14px; font-weight: 700; color: #1A1408; background: linear-gradient(150deg, var(--gold-l), var(--gold)); }
  .checkout.is-in .co-btn { animation: press 6s ease-in-out 1.6s infinite; }
  @keyframes press { 0%, 20% { transform: scale(1); } 24% { transform: scale(0.96); } 28%, 100% { transform: scale(1); } }
  .co-paid { position: absolute; left: 24px; right: 24px; bottom: 24px; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px; border-radius: 14px; font-weight: 700; background: #3E8E64; opacity: 0; transform: scale(0.9); }
  .checkout.is-in .co-paid { animation: paid 6s ease-in-out 1.6s infinite; }
  @keyframes paid { 0%, 27% { opacity: 0; transform: scale(0.9); } 32%, 80% { opacity: 1; transform: scale(1); } 88%, 100% { opacity: 0; transform: scale(0.95); } }
  .fee-note { display: flex; gap: 12px; margin-top: 24px; padding: 16px 18px; border-radius: 16px; background: rgba(201, 162, 75, 0.08); border: 1px solid rgba(201, 162, 75, 0.25); color: var(--text-2); line-height: 1.6; font-size: 0.95rem; }
  .fee-note b { color: var(--text); }

  /* ═════════════════ NUMBERS ═════════════════ */
  .numbers { padding: 70px 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); background: linear-gradient(180deg, rgba(201, 162, 75, 0.05), transparent); }
  .num-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; text-align: center; }
  .num-v { font-family: 'Space Grotesk', sans-serif; font-size: clamp(2.4rem, 5vw, 3.6rem); font-weight: 700; background: linear-gradient(180deg, #fff, var(--gold-l)); -webkit-background-clip: text; background-clip: text; color: transparent; line-height: 1.1; }
  .num-l { color: var(--text-2); font-size: 0.95rem; margin-top: 6px; }

  /* ═════════════════ PRICING TEASER ═════════════════ */
  .pricing { padding: 100px 0; }
  .pt { display: grid; grid-template-columns: 1.1fr 1fr; gap: 48px; align-items: center; padding: 44px; border-radius: 28px; background: linear-gradient(150deg, #182B49, #0F1B2E); border: 1px solid rgba(201, 162, 75, 0.3); position: relative; overflow: hidden; }
  .pt::after { content: ''; position: absolute; width: 380px; height: 380px; right: -120px; top: -160px; border-radius: 50%; background: radial-gradient(circle, rgba(201, 162, 75, 0.35), transparent 65%); filter: blur(40px); animation: drift-a 16s ease-in-out infinite; pointer-events: none; }
  .pt-price { color: var(--gold-l); }
  .pt-ctas { margin-top: 26px; }
  .pt-ctas .btn-gold { height: 56px !important; padding: 0 28px !important; }
  .pt-steps { position: relative; z-index: 1; display: grid; gap: 12px; }
  .pt-step { display: grid; grid-template-columns: 120px 1fr; grid-template-rows: auto auto; column-gap: 16px; padding: 16px 20px; border-radius: 18px; background: rgba(255, 255, 255, 0.04); border: 1px solid var(--line); opacity: 0; transform: translateX(24px); transition: all 0.7s var(--ease); }
  .pt.is-in .pt-step { opacity: 1; transform: none; }
  .pt-step:hover { border-color: rgba(201, 162, 75, 0.45); }
  .pt-n { grid-row: span 2; align-self: center; font-weight: 700; color: var(--text-2); }
  .pt-v { font-family: 'Space Grotesk', sans-serif; font-size: 1.5rem; font-weight: 700; }
  .pt-s { font-size: 0.85rem; color: var(--text-3); }

  .fade-enter-active, .fade-leave-active { transition: opacity 0.25s; }
  .fade-enter, .fade-leave-to { opacity: 0; }

  /* ═════════════════ TRUST ═════════════════ */
  .trust { padding: 100px 0; background: var(--bg-alt); }
  .trust-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
  .trust-card { position: relative; overflow: hidden; padding: 26px; border-radius: 20px; background: var(--card); border: 1px solid var(--line); transition: transform 0.4s var(--ease), border-color 0.4s var(--ease), box-shadow 0.4s var(--ease), opacity 0.8s var(--ease); }
  .trust-card h4 { font-size: 1.1rem; margin: 14px 0 8px; }
  .trust-card p { color: var(--text-2); line-height: 1.6; margin: 0; font-size: 0.93rem; }

  /* ═════════════════ FINAL CTA ═════════════════ */
  .final { position: relative; padding: 130px 0; overflow: hidden; text-align: center; }
  .final-bg { position: absolute; inset: 0; background: linear-gradient(160deg, #16233F, #1B3A5C 55%, #12283F); }
  .final-inner { position: relative; z-index: 2; max-width: 760px; }
  .final h2 { font-size: clamp(2rem, 4.4vw, 3.2rem); font-weight: 700; margin: 0 0 16px; }
  .final p { color: var(--text-2); font-size: 1.1rem; margin: 0 0 32px; }
  .final .btn-gold { padding: 0 36px !important; height: 58px !important; }

  /* ═════════════════ RESPONSIVE ═════════════════ */
  @media (max-width: 1264px) {
    .toast-0 { left: -150px; top: 20px; } .toast-1 { right: -120px; } .toast-2 { left: -140px; }
  }
  @media (max-width: 960px) {
    .hero { min-height: auto; padding-top: 30px; }
    .hero-copy { text-align: center; }
    .hero-sub { margin-left: auto; margin-right: auto; }
    .hero-ctas, .hero-trust { justify-content: center; }
    .hero-stage { margin-top: 30px; }
    .float-toast { display: none; }
    .scroll-cue { display: none; }
    .feat-grid { grid-template-columns: repeat(2, 1fr); }
    .trust-grid { grid-template-columns: repeat(2, 1fr); }
    .num-grid { grid-template-columns: repeat(2, 1fr); gap: 34px 20px; }
    .pt { grid-template-columns: 1fr; padding: 28px; gap: 28px; }
    .sec-title.left, .sec-sub.left { text-align: center; }
    .district .badge, .pay .badge { display: flex; width: max-content; margin-left: auto; margin-right: auto; }
    .rv-window { transform: none; }
    .net { margin-top: 30px; }
  }
  @media (max-width: 600px) {
    .sz-home { padding-top: 64px; }
    .roles, .features, .district, .pay, .pricing, .trust { padding: 70px 0; }
    .feat-grid, .trust-grid { grid-template-columns: 1fr; }
    .phone { width: 270px; height: 560px; }
    .hero-stage { width: 270px; }
    .role-tab { padding: 10px 14px; font-size: 0.88rem; }
    .pt-step { grid-template-columns: 1fr; }
    .pt-n { grid-row: auto; }
    .net-node b { font-size: 0.72rem; }
    .nn-ic { width: 38px; height: 38px; }
    .net-node.hq { padding: 8px 12px; font-size: 0.75rem; }
  }

  /* People who ask their device for less motion get a still page. */
  @media (prefers-reduced-motion: reduce) {
    .sz-home *, .sz-home *::before, .sz-home *::after { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; transition-duration: 0.001ms !important; }
    .rv { opacity: 1; transform: none; }
    .btn-shine::after, .spark, .qr-scan, .co-paid { display: none; }
    .net-line { stroke-dashoffset: 0; }
    .net-node, .co-line, .role-copy li, .rv-card, .hero-copy > *, .hero-stage { opacity: 1 !important; }
    .marquee-track { animation: none !important; flex-wrap: wrap; width: auto; justify-content: center; }
  }
</style>
